// Metadata-panel links list — RFC-0010 §3 (PD-0010 render half, metadata links).
//
// Renders a repository's manifest `links[]` (label / url / kind) in the project
// metadata rail. Each URL is independently re-validated to the `http(s)` scheme
// allowlist BEFORE an `<a href>` is emitted — defense-in-depth atop RFC-0003's
// parse-time allowlist and the DB `CHECK`, so a future write path (e.g. an admin
// edit tool) that bypasses those constraints still cannot produce a clickable
// `javascript:`/`data:` link here. Bad-scheme entries render as inert text, not
// anchors, so the label stays visible without becoming an XSS vector.

import type { LinkItem } from '@portal/shared-types';
import {
  ChartLine,
  Pulse,
  BookOpen,
  LinkSimple,
  type Icon,
} from '@phosphor-icons/react';
import { isSafeHttpUrl } from '@/lib/url-scheme';

const KIND_ICON: Record<LinkItem['kind'], Icon> = {
  dashboard: ChartLine,
  status: Pulse,
  'external-docs': BookOpen,
  other: LinkSimple,
};

interface MetadataLinksListProps {
  /** Accepts the serialized `LinkItem` shape (also satisfied by `Link`). */
  links: LinkItem[];
}

export function MetadataLinksList({ links }: MetadataLinksListProps) {
  if (links.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1">
      {links.map((link) => {
        const KindIcon = KIND_ICON[link.kind] ?? LinkSimple;
        const safe = isSafeHttpUrl(link.url);

        return (
          <li key={link.id}>
            {safe ? (
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-medium text-text/70 transition-colors hover:bg-overlay/5 hover:text-text"
              >
                <KindIcon size={14} className="shrink-0 text-muted" />
                <span className="truncate">{link.label}</span>
              </a>
            ) : (
              <span
                title="Link hidden — unsupported URL scheme"
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-medium text-muted"
              >
                <KindIcon size={14} className="shrink-0 text-muted" />
                <span className="truncate line-through">{link.label}</span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
